import {IonBadge, IonButton, IonIcon, IonSpinner, IonToggle} from '@ionic/react';
import {createOutline, trashOutline} from 'ionicons/icons';
import React from 'react';
import {AsteriskMessage} from '../../model/AsteriskMessage';
import ExpandableMessageText from './ExpandableMessageText';

interface AsteriskMessageListProps {
    messages: AsteriskMessage[];
    pendingIds: ReadonlySet<number>;
    onEdit: (message: AsteriskMessage) => void;
    onDelete: (message: AsteriskMessage) => void;
    onToggle: (message: AsteriskMessage, enabled: boolean) => void;
}

const sourceLabel = (message: AsteriskMessage) => message.sourceType === 'PRE_RECORDED'
    ? 'Bestaande opname'
    : 'Gegenereerde spraak';

const SourceBadge: React.FC<{message: AsteriskMessage}> = ({message}) => (
    <IonBadge color={message.sourceType === 'PRE_RECORDED' ? 'tertiary' : 'secondary'}>
        {sourceLabel(message)}
    </IonBadge>
);

const StatusControl: React.FC<{
    message: AsteriskMessage;
    isPending: boolean;
    onToggle: (message: AsteriskMessage, enabled: boolean) => void;
}> = ({message, isPending, onToggle}) => (
    <div className="asterisk-status-control">
        <span>{message.enabled ? 'Actief' : 'Inactief'}</span>
        {isPending && <IonSpinner name="crescent" aria-label="Status opslaan"/>}
        <IonToggle
            checked={message.enabled}
            disabled={isPending}
            enableOnOffLabels
            aria-label={`${message.name} ${message.enabled ? 'deactiveren' : 'activeren'}`}
            onIonChange={(event) => onToggle(message, event.detail.checked)}
        />
    </div>
);

const Actions: React.FC<{
    message: AsteriskMessage;
    disabled: boolean;
    onEdit: (message: AsteriskMessage) => void;
    onDelete: (message: AsteriskMessage) => void;
}> = ({message, disabled, onEdit, onDelete}) => (
    <div className="asterisk-row-actions">
        <IonButton
            fill="clear"
            disabled={disabled}
            aria-label={`${message.name} bewerken`}
            onClick={() => onEdit(message)}
        >
            <IonIcon icon={createOutline} slot="icon-only"/>
        </IonButton>
        <IonButton
            fill="clear"
            color="danger"
            disabled={disabled}
            aria-label={`${message.name} verwijderen`}
            onClick={() => onDelete(message)}
        >
            <IonIcon icon={trashOutline} slot="icon-only"/>
        </IonButton>
    </div>
);

const AsteriskMessageList: React.FC<AsteriskMessageListProps> = ({
    messages,
    pendingIds,
    onEdit,
    onDelete,
    onToggle,
}) => (
    <>
        <div className="asterisk-desktop-table">
            <table>
                <thead>
                    <tr>
                        <th scope="col">Naam</th>
                        <th scope="col">Gesproken tekst</th>
                        <th scope="col">Dashboardtekst</th>
                        <th scope="col">Bron</th>
                        <th scope="col">Status</th>
                        <th scope="col"><span className="sr-only">Acties</span></th>
                    </tr>
                </thead>
                <tbody>
                    {messages.map((message) => {
                        const isPending = pendingIds.has(message.id);
                        return (
                            <tr key={message.id}>
                                <th scope="row">{message.name}</th>
                                <td><ExpandableMessageText text={message.spokenText} label="Gesproken tekst"/></td>
                                <td><ExpandableMessageText text={message.displayText} label="Dashboardtekst"/></td>
                                <td><SourceBadge message={message}/></td>
                                <td><StatusControl message={message} isPending={isPending} onToggle={onToggle}/></td>
                                <td>
                                    <Actions
                                        message={message}
                                        disabled={isPending}
                                        onEdit={onEdit}
                                        onDelete={onDelete}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
        <div className="asterisk-mobile-cards">
            {messages.map((message) => {
                const isPending = pendingIds.has(message.id);
                return (
                    <article className="asterisk-message-card" key={message.id}>
                        <div className="asterisk-card-heading">
                            <h2>{message.name}</h2>
                            <SourceBadge message={message}/>
                        </div>
                        <div>
                            <h3>Gesproken tekst</h3>
                            <ExpandableMessageText text={message.spokenText} label="Gesproken tekst"/>
                        </div>
                        <div>
                            <h3>Dashboardtekst</h3>
                            <ExpandableMessageText text={message.displayText} label="Dashboardtekst"/>
                        </div>
                        <div className="asterisk-card-footer">
                            <StatusControl message={message} isPending={isPending} onToggle={onToggle}/>
                            <Actions
                                message={message}
                                disabled={isPending}
                                onEdit={onEdit}
                                onDelete={onDelete}
                            />
                        </div>
                    </article>
                );
            })}
        </div>
    </>
);

export default AsteriskMessageList;
